import { api } from '@/lib/api';

export type AssessmentStatus = 'draft' | 'submitted' | 'verified' | 'returned';

export const STATUS_LABEL: Record<AssessmentStatus, string> = {
  draft: 'Draf',
  submitted: 'Terkirim',
  verified: 'Terverifikasi',
  returned: 'Dikembalikan',
};

export interface AssessmentListItem {
  id: string;
  subjectType: 'tbm' | 'individual';
  subjectId: string;
  status: AssessmentStatus;
  totalScore: number | null;
  band: string | null;
  programYear: number | null;
  updatedAt: string;
  tbmId: string | null;
  tbmCode: string | null;
  tbmName: string | null;
  angkatan?: string | null;
  individualCode?: string | null;
  individualName?: string | null;
  instrumentName: string;
  instrumentCode: string;
  periodName: string;
}

export interface FormIndicator {
  id: string;
  number: number;
  name: string;
  description: string | null;
  weight: number;
  inputType: 'scale' | 'percentage_auto';
  criteria: { score: number; description: string }[];
  mov: { label: string; requirement: 'optional' | 'required' }[];
}

export interface FormDimension {
  id: string;
  code: string;
  name: string;
  indicators: FormIndicator[];
}

export interface FormSection {
  id: string;
  code: string;
  name: string;
  subject: 'tbm' | 'individual';
  entryMode: 'single' | 'matrix';
  weightInFinal: number;
  dimensions: FormDimension[];
}

export interface AssessmentAnswer {
  indicatorId: string;
  individualId: string | null;
  score: number | null;
  rawInput: number | null;
  note: string | null;
}

export interface AssessmentDetail {
  id: string;
  subjectType: 'tbm' | 'individual';
  subjectId: string;
  status: AssessmentStatus;
  totalScore: number | null;
  band: string | null;
  programYear: number | null;
  reviewerNote: string | null;
  subject: { code: string | null; name: string } | null;
  instrument: {
    code: string;
    name: string;
    scope: string;
    version: number;
    evidenceLabel: string | null;
    scaleLabels: { score: number; label: string }[];
    bands: { gt: number | null; name: string }[];
  };
  period: { id: string; name: string; status: string };
  members: { id: string; code: string; name: string }[];
  sections: FormSection[];
  answers: AssessmentAnswer[];
}

export interface ScoreResult {
  index: number;
  band: string | null;
  answeredCount?: number;
  totalCount?: number;
  isComplete?: boolean;
  perSection?: { code: string; value: number; subjects: number }[];
}

export interface AnswerPayload {
  indicatorId: string;
  individualId?: string | null;
  score?: number | null;
  rawInput?: number | null;
}

export const assessmentApi = {
  list: (params?: {
    periodId?: string;
    status?: string;
    tbmId?: string;
    subjectId?: string;
    subjectType?: string;
  }) => {
    const search = new URLSearchParams();
    if (params?.periodId) search.set('periodId', params.periodId);
    if (params?.status) search.set('status', params.status);
    if (params?.tbmId) search.set('tbmId', params.tbmId);
    if (params?.subjectId) search.set('subjectId', params.subjectId);
    if (params?.subjectType) search.set('subjectType', params.subjectType);
    return api.get<AssessmentListItem[]>(`/assessments?${search}`);
  },
  open: (body: {
    instrumentId: string;
    periodId: string;
    subjectType: 'tbm' | 'individual';
    subjectId: string;
  }) => api.post<{ id: string; reused: boolean; staleVersion?: boolean }>('/assessments', body),
  get: (id: string) => api.get<AssessmentDetail>(`/assessments/${id}`),
  saveAnswers: (id: string, answers: AnswerPayload[]) =>
    api.put<ScoreResult>(`/assessments/${id}/answers`, { answers }),
  submit: (id: string) =>
    api.post<{ status: string; totalScore: number; band: string | null }>(
      `/assessments/${id}/submit`,
    ),
  review: (id: string, action: 'verify' | 'return', note?: string) =>
    api.post(`/assessments/${id}/review`, { action, note: note ?? null }),
  reopen: (id: string) => api.post<{ id: string; status: string }>(`/assessments/${id}/reopen`),
  reset: (id: string) => api.post<{ id: string; status: string }>(`/assessments/${id}/reset`),
  remove: (id: string) =>
    api.del<{ deleted: number; deletedAttachments: number }>(`/assessments/${id}`),
};
